import { Form, Input, Select, Switch } from 'antd';

import { apiFetch } from '@/api/client';
import { IdDocumentTypeValues, type IdDocumentType } from '@/api/generated/enums';
import { customersKey, type Customer } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { DatePicker } from '@/components/DatePicker';
import { FormDrawer } from '@/components/FormDrawer';
import { Notice } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';

export type Editing = { mode: 'create' } | { mode: 'edit'; customer: Customer };

export interface CustomerFormValues {
  mobile: string;
  name: string;
  /** Create only — scope is fixed once the row exists, as on every other master. */
  shared?: boolean;
  alternateMobile?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  pincode?: string | null;
  gstin?: string | null;
  idDocumentType?: IdDocumentType | null;
  idDocumentNumber?: string | null;
  idDocumentExpiresOn?: unknown;
  idDocumentScanUrl?: string | null;
  source?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

/**
 * Create or correct a customer (spec Section 12).
 *
 * <p>
 * <strong>Lifted out of `CustomersPage` so the booking counter can open the same form.</strong> A
 * clerk taking a booking for somebody who has never rented before must not have to abandon the
 * booking, navigate to another screen, create the customer and start again — that is how a walk-in
 * becomes a booking filed against "Walk-in Customer" and a lifetime value nobody can reconstruct.
 * One form, two callers, and the field rules cannot drift between them.
 * </p>
 *
 * <p>
 * <strong>`onCreated` is what makes the second caller possible.</strong> The mutation already
 * receives the created row; the customers screen simply discarded it, because a list refetches. The
 * booking screen needs the id, so the created customer is now handed back and closing is left to the
 * caller.
 * </p>
 */
export function CustomerDrawer({
  editing,
  onClose,
  onCreated,
  prefillMobile,
  prefillName,
}: {
  editing: Editing;
  onClose: () => void;
  /** Called with the saved row before `onClose`. The booking screen selects it; the list ignores it. */
  onCreated?: ((customer: Customer) => void) | undefined;
  /** What the picker had been searching for, so the clerk does not retype the number they just tried. */
  prefillMobile?: string | undefined;
  prefillName?: string | undefined;
}) {
  const [form] = Form.useForm<CustomerFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.customer : undefined;

  // Watched rather than read once: the two ID fields only mean anything together, and the server
  // refuses half a document, so the number becomes required the moment a type is picked.
  const idType = Form.useWatch('idDocumentType', form);

  const save = useApiMutation<Customer, CustomerFormValues>({
    mutationFn: (values) => {
      const body = {
        ...values,
        idDocumentExpiresOn: toIsoDate(values.idDocumentExpiresOn) ?? null,
      };

      return isEdit
        ? apiFetch<Customer>(`/api/customer/${existing?.id}`, { method: 'PUT', body })
        : apiFetch<Customer>('/api/customer', { method: 'POST', body });
    },
    success: isEdit ? 'Customer updated.' : 'Customer created.',
    invalidates: [customersKey],
    onSuccess: (customer) => {
      onCreated?.(customer);
      onClose();
    },
  });

  return (
    <FormDrawer<CustomerFormValues>
      title={isEdit ? `Edit ${existing?.name ?? 'customer'}` : 'New customer'}
      open
      onClose={onClose}
      form={form}
      initialValues={
        existing
          ? { ...existing, idDocumentExpiresOn: toDayjs(existing.idDocumentExpiresOn) }
          : {
              mobile: prefillMobile ?? '',
              name: prefillName ?? '',
              shared: false,
              isActive: true,
            }
      }
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save' : 'Create'}
      submitting={save.isPending}
      width={560}
    >
      <Form.Item
        name="mobile"
        label="Mobile"
        extra="Unique across the whole company — the same person at another branch is the same customer."
        rules={[{ required: true, message: 'A mobile number is required.' }]}
      >
        <Input placeholder="98250 12345" inputMode="tel" />
      </Form.Item>

      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'A name is required.' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="alternateMobile" label="Alternate mobile">
        <Input placeholder="Someone else to ring about an overdue garment" inputMode="tel" />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input type="email" />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="shared"
          label="Shared across branches"
          valuePropName="checked"
          // Most shops want this on. Left off by default anyway, matching every other master:
          // a caller that says nothing must not publish company-wide by accident.
          extra="Recommended for a chain — one rental history and one lifetime value per person, rather than one per shop. Needs a company-wide role."
        >
          <Switch />
        </Form.Item>
      )}

      <Form.Item name="addressLine1" label="Address">
        <Input placeholder="Line 1" />
      </Form.Item>

      <Form.Item name="addressLine2" label=" " colon={false}>
        <Input placeholder="Line 2" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="city" label="City">
          <Input />
        </Form.Item>

        <Form.Item name="pincode" label="Pincode">
          <Input inputMode="numeric" />
        </Form.Item>

        <Form.Item name="stateName" label="State">
          <Input />
        </Form.Item>

        <Form.Item
          name="stateCode"
          label="State code"
          // Not decoration: place of supply is what decides CGST+SGST against IGST, and the
          // comparison is between this and the branch's own.
          extra="GST place of supply — 24 for Gujarat."
        >
          <Input maxLength={2} inputMode="numeric" />
        </Form.Item>
      </div>

      <Form.Item name="gstin" label="GSTIN" extra="Only for a customer renting against a company.">
        <Input maxLength={15} />
      </Form.Item>

      <Form.Item name="idDocumentType" label="ID document">
        <Select
          allowClear
          placeholder="None on file"
          options={IdDocumentTypeValues.map((value) => ({
            label: humanizeEnum(value),
            value,
          }))}
        />
      </Form.Item>

      {idType && (
        <>
          <Form.Item
            name="idDocumentNumber"
            label="Document number"
            rules={[{ required: true, message: 'A document needs its number.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="idDocumentExpiresOn" label="Expires on">
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item name="idDocumentScanUrl" label="Scan URL">
            <Input placeholder="https://…" />
          </Form.Item>
        </>
      )}

      <Form.Item name="source" label="How they found us">
        <Input placeholder="Walk-in, Instagram, referred by…" />
      </Form.Item>

      <Form.Item name="notes" label="Notes">
        <Input.TextArea rows={2} />
      </Form.Item>

      {isEdit && (
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      )}

      <Notice tone="info" title="Measurements and risk come later">
        Fitting measurements, group bookings and the risk band that sets a deposit multiplier are
        their own screens, and are not built yet.
      </Notice>
    </FormDrawer>
  );
}
